/** Service Desk console: the engineer's phone screens (C23: My day, visit, check-in, checklist, measurements and parts, photos, sign-off, done), SD-S3-11. */
export const serviceField = {
  title: "My day",
  back: "Back",
  loading: "Loading your visits",
  refresh: "Refresh",
  retry: "Try again",
  noOrganisation: "No organisation",
  noWindow: "No time set",
  todayList: "Today's visits",
  forbiddenTitle: "This screen is for field engineers",
  forbiddenBody: "Your role cannot work visits on the phone. Ask a workspace admin to give you the Field engineer role.",
  nav: { label: "Engineer app", entry: "My day", myDay: "My day", visit: "Visit", checklist: "Checklist", parts: "Parts", photos: "Photos" },
  tabs: { label: "Day", today: "Today", upcoming: "Upcoming" },
  empty: {
    title: "No visits assigned",
    body: "Visits scheduled for you appear here as soon as the office books them.",
    todayTitle: "No visits today",
    todayBody: "Enjoy the quiet day. New visits appear here as soon as they are scheduled.",
    upcomingTitle: "Nothing coming up",
    upcomingBody: "Visits booked for the days ahead are listed here.",
  },
  dayError: { title: "Could not load your day", body: "Check your connection and try again. Nothing was changed.", saved: "No connection: showing the visits saved on this phone." },
  error: { title: "Could not load this visit", body: "Check your connection and try again. Nothing was changed." },
  visit: { title: "Visit", notFoundTitle: "Visit not found", notFoundBody: "It may not be assigned to you any more." },
  connection: {
    online: "Online",
    offlineChip: "Offline",
    offline: "You are offline. Your report is saved on this phone and goes up when the network is back.",
    offlinePending_one: "You are offline. {{count}} item is waiting and will be sent when the network is back. Your report is saved on this phone.",
    offlinePending_other: "You are offline. {{count}} items are waiting and will be sent when the network is back. Your report is saved on this phone.",
    sending_one: "Sending {{count}} item…",
    sending_other: "Sending {{count}} items…",
    failed_one: "{{count}} item could not be sent.",
    failed_other: "{{count}} items could not be sent.",
    retry: "Try again",
  },
  leave: {
    title: "Leave with items waiting?",
    body_one: "{{count}} item is still waiting to be sent. If you leave this page it may be lost.",
    body_other: "{{count}} items are still waiting to be sent. If you leave this page they may be lost.",
    confirm: "Leave anyway",
  },
  job: { queued: "Queued", sending: "Sending…", retrying_one: "Retrying (attempt {{count}})", retrying_other: "Retrying (attempt {{count}})", waitingNetwork: "Waiting for network", failed: "Not sent", retry: "Try again", discard: "Remove" },
  save: {
    clean: "Everything is saved.",
    saved: "Saved at {{time}}",
    saving: "Saving…",
    dirty: "Not saved yet. Saving every 10 seconds.",
    offline: "Offline: saved on this phone, will send when the network is back.",
    error: "Could not save. Retrying; your text is kept on this phone.",
  },
  gate: {
    checkInFirst: "Check in first. The report opens once you are on site.",
    ended: "This visit has ended, so its report can no longer be changed.",
    openVisit: "Open the visit",
    openSummary: "Open the summary",
  },
  brief: {
    client: "Client",
    equipment: "Equipment",
    ticket: "Ticket",
    bring: "Bring and prepare",
    prep: "Prepared for the client",
    travel: "Getting there",
    internal: "Office note",
    checklist: "Checklist",
    items_one: "{{count}} item",
    items_other: "{{count}} items",
    access: "Access",
    contact: "Contact",
    navigate: "Navigate",
    call: "Call {{name}}",
    noPhone: "No phone number is on file for this contact.",
    warranty: { in_warranty: "In warranty", expiring: "Warranty ending soon", expired: "Out of warranty", unknown: "Warranty unknown" },
    checkIn: "Check in",
    onMyWay: "I am on my way",
    enRouteSent: "The office can see you are on your way.",
    checkedIn: "Checked in at {{time}}.",
    checkedInPending: "Checked in at {{time}}. Waiting for the network to send it.",
    checkInFailed: "That did not work. Try again.",
    continue: "Continue visit",
    summary: "Open the summary",
    shareLocation: "Share my location when I check in and out",
    shareLocationHint: "Coarse, stored to 3 decimals, never shown to the client. Asked once at the tap.",
    noAccess: "Nobody let me in",
  },
  noAccess: { title: "Nobody let you in?", body: "Tell the office why. A follow-up visit will wait in the queue.", confirm: "Report no access" },
  cannotComplete: { title: "You could not finish?", body: "Say what is missing (a part, a tool, access). The ticket will read Awaiting parts.", confirm: "End the visit" },
  reason: { label: "Reason", tooShort: "Write at least {{min}} characters." },
  onSite: {
    title: "On site",
    timeOnSite: "Time on site",
    checkedInAt: "Checked in {{time}}",
    notCheckedIn: "Not checked in",
    checklistNote: "{{done}} of {{total}} answered",
    measuresParts: "Measurements and parts",
    partsNote_one: "{{count}} part",
    partsNote_other: "{{count}} parts",
    photosNote_one: "{{count}} photo, {{pending}} waiting",
    photosNote_other: "{{count}} photos, {{pending}} waiting",
    work: "Work performed",
    workHint: "Required to complete the visit.",
    findings: "Findings",
    finish: "Finish and get sign-off",
    cannotFinish: "I could not finish the work",
  },
  checklist: {
    pass: "Pass",
    fail: "Fail",
    na: "N/A",
    progress: "{{pass}}/{{total}} pass",
    answered: "{{done}} of {{total}} answered",
    remaining_one: "{{count}} item is not answered. Every item needs a result before the visit can be completed.",
    remaining_other: "{{count}} items are not answered. Every item needs a result before the visit can be completed.",
    markAll: "Mark all pass",
    next: "Next: measurements",
    noteFor: "Note for {{item}}",
    notePlaceholder: "What did you find?",
    emptyTitle: "No checklist for this visit",
    emptyBody: "This visit has no template, so there is nothing to tick. Describe the work instead.",
  },
  measures: { title: "Measurements and parts", next: "Next: photos" },
  measurements: {
    title: "Measurements",
    none: "No measurements yet.",
    add: "Add measurement",
    name: "Measurement {{n}} name",
    value: "Measurement {{n}} value",
    unit: "Measurement {{n}} unit",
    namePlaceholder: "Resistivity after",
    valuePlaceholder: "18.2",
    unitPlaceholder: "MΩ·cm",
    remove: "Remove measurement {{n}}",
  },
  parts: {
    title: "Parts used",
    none: "No parts used yet.",
    addPart: "Add part",
    sheetTitle: "Add part used",
    sheetBody: "Pick it from the catalogue or type it, then say who pays.",
    part: "Part",
    partHint: "Type at least 2 letters to search the catalogue.",
    suggestions: "Catalogue matches",
    noCatalogue: "The catalogue is not available. Type the part name.",
    noMatch: "No catalogue match. Use what you typed.",
    catalogueError: "Could not search the catalogue. Type the part name.",
    freeText: "Use free text",
    quantity: "Quantity",
    coverage: "Who pays",
    choose: "Choose",
    coverageOptions: { warranty: "Warranty", contract: "Contract", chargeable: "Chargeable" },
    removedSerial: "Removed serial",
    newSerial: "New serial",
    asset: "Installed in",
    assetHint: "Adds a part replaced entry to the equipment's history.",
    noAsset: "Not tied to equipment",
    add: "Add part",
    added: "{{part}} added",
    remove: "Remove {{part}}",
    out: "out",
    in: "in",
    errors: { description: "Name the part.", quantity: "Enter a whole number from 1 to 999.", coverage: "Choose who pays: warranty, contract or chargeable." },
  },
  time: {
    title: "Time on this visit",
    none: "No time recorded yet.",
    addTime: "Add time",
    sheetTitle: "Add time",
    sheetBody: "Time on site, travel or remote work for this visit.",
    minutes: "Minutes",
    minutesError: "Enter whole minutes from 1 to 1440.",
    quick: "Quick amounts",
    activity: "What kind of time",
    activities: { remote: "Remote", travel: "Travel", on_site: "On site", admin: "Admin" },
    billable: "Billable",
    billableChip: "Billable",
    note: "Note",
    add: "Add time",
    added: "Time added",
    remove: "Remove {{time}}",
    hourShort: "h",
    minuteShort: "min",
  },
  photos: {
    count_one: "{{count}} photo",
    count_other: "{{count}} photos",
    list: "Photos of this visit",
    emptyTitle: "No photos yet",
    emptyBody: "Take pictures of the equipment, the work and the result. They are saved on this phone first.",
    take: "Take photo",
    choose: "Choose from gallery",
    full: "A visit holds at most {{max}} photos.",
    clientVisible: "Show new photos in the client's report",
    clientVisibleHint: "Off: only the office sees them.",
    queued_one: "{{count}} photo saved on this phone and sending",
    queued_other: "{{count}} photos saved on this phone and sending",
    notImage: "{{name}} is not a picture.",
    uploaded: "Uploaded",
    inReport: "In client report",
    remove: "Remove {{name}}",
    removeTitle: "Remove this photo?",
    removeBody: "It is taken off the visit and its file is deleted.",
    removeConfirm: "Remove photo",
    note: "Nothing is lost while this page stays open. Leaving with photos waiting asks you to confirm.",
    pending_one: "{{count}} pending",
    pending_other: "{{count}} pending",
    allUploaded: "All photos uploaded",
    notSent_one: "{{count}} photo was not sent",
    notSent_other: "{{count}} photos were not sent",
    next: "Next: sign-off",
  },
  sign: {
    title: "Client sign-off",
    clear: "Clear",
    summary: "Checklist {{pass}}/{{total}} pass, {{parts}} parts, {{photos}} photos.",
    cannotComplete: "The visit cannot be completed yet",
    workMissing: "Describe the work performed.",
    openNotes: "Open notes",
    checklistMissing_one: "{{count}} checklist item is not answered.",
    checklistMissing_other: "{{count}} checklist items are not answered.",
    openChecklist: "Open checklist",
    offline: "You are offline. Signing completes the visit on the server, so reconnect first. Nothing you typed is lost.",
    waitingActions_one: "{{count}} item (check-in, part or time) must reach the server first. It is sending.",
    waitingActions_other: "{{count}} items (check-in, parts or time) must reach the server first. They are sending.",
    fixFirst: "Fix the items above, then the client can sign.",
    photosPending_one: "{{count}} photo is still uploading. It will join the visit when it arrives, but it will not be in this report.",
    photosPending_other: "{{count}} photos are still uploading. They will join the visit when they arrive, but they will not be in this report.",
    outcome: "Outcome",
    resolved: "Issue resolved",
    followUp: "Follow-up visit needed",
    followUpNote: "What is needed next",
    includeTime: "Show the time on site in the client's report",
    shareContact: "Show my mobile number to the client",
    shareContactHint: "Off by default. Only for this visit.",
    method: "Signature method",
    type: "Type your name",
    draw: "Draw",
    typedLabel: "Client types their full name",
    nameLabel: "Client's full name",
    titleLabel: "Job title (optional)",
    previewEmpty: "The name appears here",
    padLabel: "Signature pad. Use the Type your name option if you cannot draw.",
    padHint: "Cannot draw? Use Type your name.",
    agree: "I confirm the work above was done.",
    errors: { typedName: "Ask the client to type their full name.", name: "Type the client's full name.", image: "The client needs to sign in the box, or use Type your name.", agree: "The client confirms the work by ticking the box." },
    submit: "Client signs and complete",
    remote: "Send a remote sign-off link instead",
    saveFirst: "The report could not be saved. Check your connection and try again.",
    completed: "{{visit}} completed",
  },
  done: {
    title: "Done",
    heading: { completed: "Visit completed", cannot_complete: "Visit ended: could not finish", no_access: "Visit ended: no access", on_site: "Visit under way", scheduled: "Visit scheduled", dispatched: "On the way", proposed: "Waiting for a window", cancelled: "Visit cancelled" },
    completedBody: "The report for {{visit}} was made and the office has it.",
    onSite: "On site",
    checklist: "Checklist",
    parts: "Parts",
    recorded_one: "{{count}} recorded",
    recorded_other: "{{count}} recorded",
    photos: "Photos",
    signedBy: "Signed by",
    remotePending: "Remote link sent",
    openReport: "Open the report (PDF)",
    notYet: "Finish the sign-off to complete the visit.",
    finish: "Go to sign-off",
  },
  errors: {
    report_incomplete: "Describe the work performed and answer every checklist item before completing.",
    report_locked: "The report was submitted and can no longer be changed.",
    visit_ended: "This visit has ended, so it cannot change any more.",
    visit_transition: "The visit cannot move to that step from where it is.",
    visit_not_yours: "This visit is not assigned to you.",
    already_checked_in: "You are already checked in to this visit.",
    already_checked_out: "You already checked out of this visit.",
    not_on_site: "Check in before you report from the site.",
    reason_needed: "Write a reason of at least 3 characters.",
    photo_not_image: "A photo must be a picture file.",
    beyond_contract: "This visit goes beyond the contract. The office will decide how it is counted.",
    forbidden: "Your role cannot do that on the phone.",
  },
} as const;
